import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import documentRequirementsService from '../services/documentRequirementsService';
import clubDocumentApi from '../services/clubDocumentApi';
import { DocumentCompletionWizard } from '../components/club-documents/v2';
import LoadingState from '../components/LoadingState';
import styles from './DocumentCompletion.module.css';

/**
 * DocumentCompletion Page
 * 
 * Standalone page for completing document requirements using the multi-step wizard.
 * Accessed from /my-documents page when user clicks "Complete Now" button.
 */
const DocumentCompletion = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { assignmentId } = useParams();
  
  const [clubData, setClubData] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDocumentData();
  }, [assignmentId]);

  const loadDocumentData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user and club info
      const userInfo = await apiService.getCurrentUser();
      const club_id = userInfo.club_id;

      if (!club_id) {
        setError('Club information not found');
        return;
      }

      // Load club info
      const clubResponse = await apiService.get('/api/clubs/public/club-info');
      const clubInfo = {
        id: club_id,
        name: clubResponse.name || clubResponse.display_name || 'Club Name',
        club_id: club_id
      };
      setClubData(clubInfo);

      // Load the specific assignment
      if (!assignmentId) {
        setError('Assignment ID not provided');
        return;
      }

      const assignmentsData = await documentRequirementsService.getMyAssignments();
      console.log('[DocumentCompletion] All assignments:', assignmentsData);
      console.log('[DocumentCompletion] Looking for assignment ID:', assignmentId);
      console.log('[DocumentCompletion] Assignment IDs:', assignmentsData.map(a => a.id));

      const targetAssignment = assignmentsData.find(a => a.id === assignmentId);
      console.log('[DocumentCompletion] Found assignment:', targetAssignment);

      if (!targetAssignment) {
        console.error('[DocumentCompletion] Assignment not found in user assignments list');
        setError('Document assignment not found');
        return;
      }

      // Get requirement details
      const requirement = await documentRequirementsService.getRequirement(
        targetAssignment.requirement_id
      );

      // Get template details
      let template = null;
      try {
        const templatesResponse = await clubDocumentApi.getTemplates(clubInfo.tenant_id);
        const responseData = templatesResponse.data || templatesResponse;
        const clubTemplates = responseData.templates || [];
        template = clubTemplates.find(t => t.id === requirement.template_id);
      } catch (err) {
        console.error('Error loading template:', err);
      }

      // Enrich assignment with details
      const enrichedAssignment = {
        ...targetAssignment,
        requirement,
        template: template || null
      };

      setAssignment(enrichedAssignment);
    } catch (err) {
      console.error('Error loading document data:', err);
      setError('Failed to load document information');
    } finally {
      setLoading(false);
    }
  };

  const handleWizardClose = () => {
    // Return to documents list
    navigate('/my-documents');
  };

  const handleWizardSuccess = async (result) => {
    // Reload documents list and show success message
    navigate('/my-documents?completed=true', { 
      state: { completionSuccess: true }
    });
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80vh'
        }}>
          <LoadingState
            variant="inline"
            size="large"
            message="Loading document..."
            spinnerColor="primary"
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <h2>Error</h2>
          <p>{error}</p>
          <button 
            onClick={() => navigate('/my-documents')}
            className={styles.backButton}
          >
            ← Back to Documents
          </button>
        </div>
      </div>
    );
  }

  if (!assignment || !clubData) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <h2>Document Not Found</h2>
          <p>The requested document could not be found.</p>
          <button 
            onClick={() => navigate('/my-documents')}
            className={styles.backButton}
          >
            ← Back to Documents
          </button>
        </div>
      </div>
    );
  }

  // Prepare document object for wizard
  const document = {
    ...assignment.template,
    id: assignment.id,  // Override template.id with assignment.id
    assignment_id: assignment.id,
    requirement_id: assignment.requirement_id,
    template_id: assignment.requirement?.template_id || assignment.template?.id
  };

  return (
    <div className={styles.container}>
      <DocumentCompletionWizard
        document={document}
        template={assignment.template}
        templates={[assignment.template]}
        clubs={clubData ? [clubData] : []}
        club_id={clubData.id}
        onClose={handleWizardClose}
        onSuccess={handleWizardSuccess}
      />
    </div>
  );
};

export default DocumentCompletion;
